import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import download from "downloadjs";
import { SearchNavBar } from "../../components/ui";
import {
  AppStrings,
  getCurrentIP,
  getLrsUtilizationEchoFields,
  logApiErrorToSentry,
  removeKeys,
} from "../../utils";
import CalculationSection from "./CalculationSection";
import ReviewOverview from "./ReviewOverview";
import { remittanceActions } from "../../state/remittanceSlice";
import Typography from "../../../../components/ui/Typography";
import {
  ConsentModal,
  TermsAndCondition,
  RemitterEsignModal,
  DigioEsignModal,
} from "../../components/modal";
import { remittanceApi } from "../../api/remittanceApi";
import MixpanelConfig from "../../../../constants/MixpanelConfig";
import { useDigioEsignEnabled } from "../../hooks/useDigioEsignEnabled";
import { digioApi } from "../../../../services/digio";
import { isDigioSignLinkSent, shouldUseDigioSignedA2FormApi } from "../../utils/digioStatus";
import { isAdIIPurpose } from "../../utils/remitterKycFlow";

const RemittanceReview: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isDigioEsignEnabled = useDigioEsignEnabled();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient, review, documents } = useAppSelector(
    (state) => state.remittance
  );
  const { amountCalculation } = calculator;
  const { selectedRemitter } = remitter;
  const { selectedRecipient } = recipient;
  const { remitterSignedStatus } = review;
  const transVendorKey = amountCalculation?.transVendorKey ?? "";
  const isAdII = isAdIIPurpose(amountCalculation?.purposeDropdown?.adCategory);
  const vendorConfigLoading =
    isAdII && Boolean(remitter?.getTransLoggedInVendorConfig?.loading);
  const vendorConfigReady =
    !isAdII ||
    remitter?.getTransLoggedInVendorConfig?.data != null ||
    remitter?.getTransLoggedInVendorConfig?.error != null ||
    !transVendorKey;

  {
    /* AD-II vendor config — Digio e-sign gate. AD-I uses legacy A2 e-sign only. */
  }
  useEffect(() => {
    const fetchAdIIVendorConfig = async () => {
      if (!isAdII || !transVendorKey) return;

      try {
        dispatch(
          remittanceActions.setGetTransLoggedInVendorConfig({
            loading: true,
            error: null,
          })
        );
        const response = await remittanceApi.getAdIIVendorConfig(transVendorKey);
        dispatch(
          remittanceActions.setGetTransLoggedInVendorConfig({
            data: response?.data ?? null,
            loading: false,
            error: null,
          })
        );
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.error?.type ||
          err?.response?.error?.type ||
          err?.response?.error ||
          AppStrings.GENERIC_ERROR;
        dispatch(
          remittanceActions.setGetTransLoggedInVendorConfig({
            loading: false,
            error: errorMsg,
            data: null,
          })
        );
        logApiErrorToSentry(err, "remittanceApi.getAdIIVendorConfig");
      }
    };

    fetchAdIIVendorConfig();
  }, [isAdII, transVendorKey, dispatch]);

  const loading =
    review?.remitterSignedStatus?.loading ||
    review?.consentData?.loading ||
    vendorConfigLoading ||
    (isDigioEsignEnabled && review?.digioEsign?.loading);
  const error =
    review?.remitterSignedStatus?.error ||
    review?.consentData?.error ||
    (isDigioEsignEnabled ? review?.digioEsign?.error : null);

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber !== 5) {
      return;
    }
    if (isAdII && (vendorConfigLoading || !vendorConfigReady)) {
      return;
    }

    const userKey = remitter?.selectedRemitter?.userKey;
    if (userKey) {
      if (isDigioEsignEnabled) {
        if (review?.digioEsign?.data?.uniqueKey) {
          dispatch(remittanceActions.fetchDigioStatusRequested());
        }
      } else {
        dispatch(remittanceActions.fetchRemitterSignedStatusRequested(userKey));
      }
    }
    dispatch(remittanceActions.fetchGetModalContentRequested("KNOW_MORE"));
    getIpData();
  }, [
    dispatch,
    isDigioEsignEnabled,
    remitter?.selectedRemitter?.userKey,
    remitterState?.progresStep?.activeStepNumber,
    isAdII,
    vendorConfigLoading,
    vendorConfigReady,
  ]);

  const getIpData = async () => {
    const currentIP = await getCurrentIP();
    dispatch(remittanceActions.setReviewDetailsInput({ currentIP }));
  };

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 5) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber, navigate]);

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber !== 5 || !isDigioEsignEnabled) {
      return;
    }

    const uniqueKey = review?.digioEsign?.data?.uniqueKey;
    const isAwaitingSign = isDigioSignLinkSent(review?.digioEsign?.data?.status);
    const isModalOpen = remitterState?.modalType === "DIGIO_ESIGN_MODAL";

    if (!uniqueKey || !isAwaitingSign || isModalOpen) {
      return;
    }

    const interval = setInterval(() => {
      dispatch(remittanceActions.fetchDigioStatusRequested());
    }, 3000);

    return () => clearInterval(interval);
  }, [
    dispatch,
    isDigioEsignEnabled,
    remitterState?.modalType,
    remitterState?.progresStep?.activeStepNumber,
    review?.digioEsign?.data?.status,
    review?.digioEsign?.data?.uniqueKey,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  {
    /* getTransactionSummary */
  }
  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber !== 5) {
      return;
    }

    const userKey = remitter?.selectedRemitter?.userKey;
    if (!userKey) {
      return;
    }

    const isSend = calculator?.amountCalculation?.changeInputFrom === "send";
    const isReceive = calculator?.amountCalculation?.changeInputFrom === "receive";

    if (
      (isSend && calculator?.amountCalculation?.user_send === undefined) ||
      (isReceive && calculator?.amountCalculation?.reciepient_recieve === undefined) ||
      !calculator?.amountCalculation?.referrerVendor ||
      !calculator?.amountCalculation?.transaction_purpose ||
      calculator?.amountCalculation?.skipApi
    ) {
      return;
    }

    const amountCalculationPayload = { ...calculator?.amountCalculation };
    const isUserSendORRecipient =
      isSend && !calculator?.amountCalculation?.lock_reciepient_recieve
        ? "reciepient_recieve"
        : "user_send";
    const keys = [
      isUserSendORRecipient,
      "changeInputFrom",
      "countryDropdown",
      "purposeDropdown",
      "vendorDropdown",
      "lock_reciepient_recieve",
      "skipApi",
    ];
    const formData = removeKeys(amountCalculationPayload, keys);
    formData.declare_amount = Number(formData.declare_amount) || 0;
    formData.userId = userKey;

    const debounce = setTimeout(() => {
      dispatch(remittanceActions.fetchTransactionSummaryRequested(formData));
    }, 0);

    return () => clearTimeout(debounce);
  }, [
    dispatch,
    remitterState?.progresStep?.activeStepNumber,
    remitter?.selectedRemitter?.userKey,
    calculator?.amountCalculation?.user_send,
    calculator?.amountCalculation?.reciepient_recieve,
    calculator?.amountCalculation?.lock_reciepient_recieve,
    calculator?.amountCalculation?.transaction_purpose,
    calculator?.amountCalculation?.isEducation,
    calculator?.amountCalculation?.conversion_rate,
    calculator?.amountCalculation?.referrerVendor,
    calculator?.amountCalculation?.isBene,
    calculator?.amountCalculation?.pan_number,
    calculator?.amountCalculation?.declare_amount,
    calculator?.amountCalculation?.skipApi,
    calculator?.amountCalculation?.changeInputFrom,
  ]);

  const shouldUseDigioA2FormApi = () =>
    shouldUseDigioSignedA2FormApi(isDigioEsignEnabled, review?.digioEsign?.data?.status);

  const handleDigioA2Form = async (type: "view" | "download") => {
    const documentId = review?.digioEsign?.data?.digioDocId;
    if (!documentId) {
      toast.error("Signed A2 form is not available yet.");
      return;
    }

    const setLoading =
      type === "view"
        ? remittanceActions.setViewA2FormState
        : remittanceActions.setDowanloadA2FormState;

    try {
      dispatch(setLoading({ loading: true, error: null }));
      const response = await digioApi.downloadDocument(documentId);
      const content = response.headers["content-type"] || "application/pdf";

      if (type === "view") {
        const file = new Blob([response.data], { type: content });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      } else {
        download(response.data, "signed_A2_form.pdf", content);
      }

      dispatch(setLoading({ loading: false, error: null }));
    } catch (error: any) {
      const message = error?.response?.data?.error?.type || "Unable to fetch signed A2 form.";
      dispatch(setLoading({ loading: false, error: message }));
      toast.error(message);
      logApiErrorToSentry(error, type === "view" ? "viewDigioA2Form" : "downloadDigioA2Form");
    }
  };

  const viewORDownloadA2Form = async (type: string) => {
    const action = type === "download" ? "download" : "view";

    if (shouldUseDigioA2FormApi()) {
      await handleDigioA2Form(action);
      return;
    }

    try {
      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: true, error: null }));
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: true, error: null }));
      }

      const totalYouPay = calculator?.amountCalculation?.roundOff
        ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find(
            (item: any) => item.id === 10
          )?.total_pay
        : calculator?.getTransactionSummary?.data?.total_pay;
      const params = new URLSearchParams({
        city: selectedRemitter?.city ?? "",
        dest_curr: amountCalculation?.dest_currency ?? "",
        pan_num: amountCalculation?.pan_number ?? "",
        pincode: selectedRemitter?.pinCode ?? "",
        purpose_code: amountCalculation?.purposeCode ?? "",
        purpose_description: amountCalculation?.transaction_purpose ?? "",
        reciepient_recieve: `${calculator?.getTransactionSummary?.data?.reciepient_recieve ?? ""}`,
        recipeint_accnt_num: selectedRecipient?.bankAccountNumber ?? "",
        recipient_bank_name: selectedRecipient?.bankName ?? "",
        recipient_first_name: selectedRecipient?.firstName ?? "",
        recipient_last_name: selectedRecipient?.lastName ?? "",
        reicpient_bank_accnt_country: selectedRecipient?.bankAccountCountry ?? "",
        state: selectedRecipient?.bankAccountCountry ?? "",
        total_pay: `${totalYouPay ?? ""}`,
        user_address1: encodeURIComponent(selectedRemitter?.addressLine1) ?? "",
        user_first_name: selectedRemitter?.firstName ?? "",
        user_last_name: selectedRemitter?.lastName ?? "",
        recipientId: selectedRecipient?.id ?? "",
        bene: amountCalculation?.isBene ? "true" : "false",
        sourceOfFunds: review?.reviewDetails?.sourceOfFundsDropdown?.label ?? "",
      });

      const response = await remittanceApi.getViewA2form(
        remitter?.selectedRemitter?.userKey ?? "",
        params
      );

      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: false, error: null }));
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: false, error: null }));
        const content = response.headers["content-type"];
        download(response.data, "download_A2.pdf", content);
      }
    } catch (error: any) {
      let message = "An unexpected error occurred in Viewing A2.";
      if (error?.response?.data?.error?.type) {
        message = error.response.data.error.type;
      }
      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: false, error: null }));
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: false, error: null }));
      }
      toast.error(message);
      logApiErrorToSentry(error, "viewORDownloadA2Form");
    }
  };

  const initializeTransaction = async () => {
    try {
      dispatch(remittanceActions.setTransactionDetail({ data: null, error: null, loading: true }));
      const formData = {
        transVendorKey: amountCalculation?.vendorDropdown?.userId,
        advance_pay: 0,
        base_currency: amountCalculation?.base_currency,
        cgst: calculator?.getTransactionSummary?.data?.cgst,
        conversion_rate: calculator?.getTransactionSummary?.data?.conversion_rate,
        dest_currency: calculator?.getTransactionSummary?.data?.dest_currency,
        hop_fees: calculator?.getTransactionSummary?.data?.hop_fees,
        igst_fees: calculator?.getTransactionSummary?.data?.igst_fee,
        reciepient_recieve: calculator?.getTransactionSummary?.data?.reciepient_recieve,
        service_tax: calculator?.getTransactionSummary?.data?.service_tax,
        sgst: calculator?.getTransactionSummary?.data?.sgst,
        tcs: calculator?.getTransactionSummary?.data?.tcs,
        total_pay: calculator?.getTransactionSummary?.data?.total_pay,
        total_tax: calculator?.getTransactionSummary?.data?.total_tax,
        mgr_initial: calculator?.getTransactionSummary?.data?.mgr_initial,
        ...getLrsUtilizationEchoFields(calculator?.getTransactionSummary?.data),
        transaction_purpose: calculator?.getTransactionSummary?.data?.transaction_purpose,
        purposeCode: calculator?.getTransactionSummary?.data?.purposeCode,
        transactionLeadIds: documents?.recipientLeadDocs.flatMap((i) => i.leadId ?? []),
        isA2FormConsent: true,
        user_send: calculator?.getTransactionSummary?.data?.user_send,
        isAdvancePay: false,
        isFullPay: true,
        hop_coins: calculator?.getTransactionSummary?.data?.hop_coins,
        isEducation: amountCalculation?.isEducation,
        id_recipient: recipient?.selectedRecipient?.id + "",
        conversion_amount: calculator?.getTransactionSummary?.data?.conversion_amount,
        nostro_charges: calculator?.getTransactionSummary?.data?.nostro_charges,
        nostro_charges_new: calculator?.getTransactionSummary?.data?.nostro_charges_new,
        promo_code: calculator?.getTransactionSummary?.data?.promo_code,
        promo_code_disount: calculator?.getTransactionSummary?.data?.promo_code_discount,
        vendor_margin: calculator?.getTransactionSummary?.data?.vendor_margin,
        vendor_key: authState?.currentUser?.vendor_key,
        referrerVendor: amountCalculation?.vendorDropdown?.vendorCode ?? "",
        isBene: amountCalculation?.isBene,
        ipAddress: review?.reviewDetails?.currentIP,
        sourceOfFunds: review?.reviewDetails?.sourceOfFundsDropdown?.label,
        trxnDeclarations: {
          vendorCode: amountCalculation?.vendorDropdown?.vendorCode ?? "",
          lanNumber: review?.reviewDetails?.lanNumber ?? "",
          paymentUtr: review?.reviewDetails?.utrNumber ?? "",
        },
        nextDecimal: amountCalculation?.roundOff ? 10 : 0,
        consent: [
          {
            store: true,
            accepted: review?.reviewDetails?.consentForm,
            pageName: "COMPLIANCE",
            ipAddress: review?.reviewDetails?.currentIP,
          },
        ],
        ...(remitter?.selectedQuoteUniqueKey
          ? { quote_unique_key: remitter.selectedQuoteUniqueKey }
          : {}),
        ...(isDigioEsignEnabled && review?.digioEsign?.data?.id
          ? { digio_esign_id: review.digioEsign.data.id }
          : {}),
      };
      MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Review_Submit, {
        authState: authState,
        remitterState: remitterState,
        formData: formData,
      });
      const response = await remittanceApi.initializeTransaction(
        remitter?.selectedRemitter?.userKey,
        formData
      );
      if (response?.data) {
        dispatch(remittanceActions.setActiveStepNumber(6));
        dispatch(
          remittanceActions.setTransactionDetail({
            data: { ...response?.data, remitterId: remitter?.selectedRemitter?.userKey },
            error: null,
            loading: false,
          })
        );
        if (response?.data?.transactionNumber) {
          MixpanelConfig.trackEvent(response?.data?.transactionNumber, {
            authState: authState,
            remitterState: remitterState,
            formData: formData,
            transactionNumber: response?.data?.transactionNumber,
            id_transaction: response?.data?.id_transaction,
            from: "NEW_STATE_MANAGEMENT",
          });
        }
        navigate("/send/payment");
      } else {
        toast.error(AppStrings.GENERIC_ERROR);
        dispatch(
          remittanceActions.setTransactionDetail({ data: null, error: null, loading: false })
        );
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setTransactionDetail({ data: null, error: null, loading: false }));
      logApiErrorToSentry(err, "initializeTransaction");
    }
  };

  return (
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px] scale-90 -mt-10">
        <ToastContainer autoClose={3000} position="top-right" />

        <SearchNavBar
          title={AppStrings.REVIEW_SCREEN.REVIEW_CALCULATION}
          addButtonTitle={AppStrings.BUTTON.SUBMIT}
          titleAndButton={false}
          clickButton={false}
        />

        {error && (
          <div className="p-4 flex justify-center items-center">
            <Typography tagType="div" text={error} className="text-sm font-normal text-red-100" />
          </div>
        )}

        <div className="flex justify-center">
          <CalculationSection />
          <div className="w-1 h-[580px] border-4 border-r mx-10" />
          <ReviewOverview
            viewORDownloadA2Form={viewORDownloadA2Form}
            initializeTransaction={initializeTransaction}
            isDigioEsignEnabled={isDigioEsignEnabled}
          />
        </div>
      </div>

      <TermsAndCondition
        open={remitterState?.modalType === "TERMS_AND_CONDITIONS"}
        onYes={() => {
          dispatch(remittanceActions.setModalType(""));
          dispatch(remittanceActions.setReviewDetailsInput({ termsAndConditions: true }));
        }}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
      />

      <ConsentModal
        open={remitterState?.modalType === "CONSENT_FORM"}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
        contentData={review?.consentData?.data}
      />

      <RemitterEsignModal
        handleA2Form={() => viewORDownloadA2Form("download")}
        visible={!isDigioEsignEnabled && remitterState?.modalType == "REMITTER_ESIGN_MODAL"}
        a2Link={review?.remitterSignedStatus?.data?.link ?? ""}
        onClose={() => {
          dispatch(remittanceActions.setModalType(""));
          const userKey = remitter?.selectedRemitter?.userKey;
          if (userKey) {
            dispatch(remittanceActions.fetchRemitterSignedStatusRequested(userKey));
          }
        }}
      />

      <DigioEsignModal
        visible={isDigioEsignEnabled && remitterState?.modalType === "DIGIO_ESIGN_MODAL"}
        onDownloadA2Form={() => viewORDownloadA2Form("download")}
        onClose={() => {
          dispatch(remittanceActions.setModalType(""));
          if (review?.digioEsign?.data?.uniqueKey) {
            dispatch(remittanceActions.fetchDigioStatusRequested());
          }
        }}
      />
    </>
  );
};

export default RemittanceReview;
